import * as React from "react";
import { AlertTriangle, Info } from "lucide-react";
import { Modal } from "../../../../ui/components/Modal";
import { Text } from "../../../../ui/components/Text";
import { Button } from "../../../../ui/components/Button";
import { Checkbox } from "../../../../ui/components/Checkbox";
import { TextInput } from "../../../../ui/components/Form";
import { useI18n } from "../../../lib/i18n";
import {
  sendProductCommand,
  type CommandExecution,
  type ProductCommand,
} from "../../../lib/api/commands";
import { CommandDangerPill } from "../../commands/CommandPills";
import {
  CommandParamForm,
  initialParamValues,
  missingRequiredParams,
  paramValuesToRequest,
  type CommandParamValues,
} from "../../commands/CommandParamForm";
import * as s from "../../commands/commands.css";

export function CommandSendModal({
  command,
  productId,
  token,
  onClose,
  onSent,
}: {
  command: ProductCommand | null;
  productId: string;
  token: string;
  onClose: () => void;
  onSent: (execution: CommandExecution) => void;
}) {
  const { t } = useI18n();
  const [values, setValues] = React.useState<CommandParamValues>({});
  const [acknowledged, setAcknowledged] = React.useState(false);
  const [typedKey, setTypedKey] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [errorField, setErrorField] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!command) return;
    setValues(initialParamValues(command.params));
    setAcknowledged(false);
    setTypedKey("");
    setError(null);
    setErrorField(null);
  }, [command]);

  if (!command) return null;

  const missing = missingRequiredParams(command.params, values);
  const confirmed =
    command.dangerLevel === "normal" ||
    (command.dangerLevel === "disruptive" && acknowledged) ||
    (command.dangerLevel === "irreversible" && typedKey.trim() === command.key);
  const canSend = !sending && missing.length === 0 && confirmed;

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSend) return;
    setSending(true);
    setError(null);
    setErrorField(null);
    try {
      const execution = await sendProductCommand(token, productId, {
        definitionId: command.id,
        params: paramValuesToRequest(command.params, values),
      });
      onSent(execution);
    } catch (err) {
      const data = (err as { data?: { field?: string; inFlightCommand?: string } }).data;
      setErrorField(data?.field ?? null);
      setError(
        data?.inFlightCommand
          ? t("pro.commands.send.busy", { command: data.inFlightCommand })
          : (err as Error).message || t("pro.commands.send.failed"),
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal open setOpen={(open) => !open && !sending && onClose()} minWidth="480px" maxWidth="640px" closeOnClickOff>
      <form className={s.modalBody} onSubmit={handleSend}>
        <div className={s.commandCardHeader}>
          <div>
            <Text level="h2" weight="bold">
              {command.displayName}
            </Text>
            <span className={s.subtle}>{command.key}</span>
          </div>
          <CommandDangerPill level={command.dangerLevel} />
        </div>

        {command.description && <Text level="body-medium">{command.description}</Text>}

        {command.unverified.length > 0 && (
          <div className={s.bannerWarning}>
            <Info size={16} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>{t("pro.commands.send.unverified", { checks: command.unverified.join(", ") })}</span>
          </div>
        )}

        <CommandParamForm
          fields={command.params}
          values={values}
          onChange={setValues}
          disabled={sending}
          errorField={errorField}
        />

        {command.hasPreRead && <span className={s.help}>{t("pro.commands.send.preReadNote")}</span>}

        {command.dangerLevel !== "normal" && (
          <div className={command.dangerLevel === "irreversible" ? s.bannerDanger : s.bannerWarning}>
            <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
            <div className={s.field} style={{ gap: 10 }}>
              <span>{command.confirmationText ?? t("pro.commands.send.defaultWarning")}</span>
              {command.dangerLevel === "disruptive" ? (
                <label className={s.inlineRow}>
                  <Checkbox checked={acknowledged} onCheckedChange={setAcknowledged} disabled={sending} />
                  <span>{t("pro.commands.send.acknowledge")}</span>
                </label>
              ) : (
                <TextInput
                  label={t("pro.commands.send.typeToConfirm", { key: command.key })}
                  value={typedKey}
                  onChange={(e) => setTypedKey(e.target.value)}
                  disabled={sending}
                  autoComplete="off"
                />
              )}
            </div>
          </div>
        )}

        {error && <div className={s.bannerDanger}>{error}</div>}

        <div className={s.modalFooter}>
          <Button type="button" color="neutral" onClick={onClose} disabled={sending}>
            {t("shared.common.cancel")}
          </Button>
          <Button
            type="submit"
            color={command.dangerLevel === "normal" ? "primary" : "danger"}
            disabled={!canSend}
          >
            {sending ? t("pro.commands.send.sending") : t("pro.commands.send.submit")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
